import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  MessageSquare, 
  Search, 
  Settings,
  Heart,
  Bell,
  Star,
  CreditCard,
  FileText,
  HelpCircle,
  X
} from 'lucide-react';

const ClientBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const navigationItems = [
    {
      name: 'Account',
      href: '/dashboard',
      icon: User
    },
    {
      name: 'Bookings',
      href: '/bookings',
      icon: Calendar
    },
    {
      name: 'Messages',
      href: '/messages',
      icon: MessageSquare
    },
    {
      name: 'Browse',
      href: '/browse-vendors',
      icon: Search
    }
  ];

  const moreMenuItems = [
    {
      name: 'Favorites',
      href: '/favorites',
      icon: Heart
    },
    {
      name: 'Notifications',
      href: '/notifications',
      icon: Bell
    },
    {
      name: 'My Reviews',
      href: '/reviews',
      icon: Star
    },
    // {
    //   name: 'Billing Details',
    //   href: '/billing',
    //   icon: CreditCard
    // },
    // {
    //   name: 'Transaction History',
    //   href: '/transactions',
    //   icon: FileText
    // },
    {
      name: 'Help & Support',
      href: '/help',
      icon: HelpCircle
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings
    }
  ];

  const handleMoreClick = (e) => {
    e.preventDefault();
    setIsMoreMenuOpen(true);
  };

  const handleMenuItemClick = (href) => {
    navigate(href);
    setIsMoreMenuOpen(false);
  };

  const isMoreActive = moreMenuItems.some(item => 
    location.pathname === item.href || 
    (item.href !== '/' && location.pathname.startsWith(item.href))
  );

  // Only show on mobile screens
  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 z-50">
        <div className="flex items-center justify-around px-1 py-2">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/' && location.pathname.startsWith(item.href));
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center px-2 py-1.5 !rounded-xl transition-colors min-w-0 flex-1 relative ${
                  isActive
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <item.icon className={`w-5 h-5 mb-1 ${isActive ? 'text-sky-600 dark:text-sky-400' : 'text-gray-600 dark:text-gray-400'}`} />
                <span className={`text-xs font-medium truncate w-full text-center ${isActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                  {item.name}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-sky-600 dark:bg-sky-400 !rounded-xl"></div>
                )}
              </NavLink>
            );
          })}
          
          {/* More Button */}
          <button
            onClick={handleMoreClick}
            className={`flex flex-col items-center justify-center px-2 py-1.5 !rounded-xl transition-colors min-w-0 flex-1 relative ${
              isMoreActive
                ? 'text-gray-900 dark:text-gray-100'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <Settings className={`w-5 h-5 mb-1 ${isMoreActive ? 'text-sky-600 dark:text-sky-400' : 'text-gray-600 dark:text-gray-400'}`} />
            <span className={`text-xs font-medium truncate w-full text-center ${isMoreActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
              More
            </span>
            {isMoreActive && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-sky-600 dark:bg-sky-400 !rounded-xl"></div>
            )}
          </button>
        </div>
      </nav>

      {/* More Menu Modal */}
      {isMoreMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 dashboard-surface z-[11000] flex items-end"
          onClick={() => setIsMoreMenuOpen(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal Content */}
          <div
            className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl max-h-[70vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-4 flex items-center justify-between z-10">
              <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">More Options</h3>
              <button
                onClick={() => setIsMoreMenuOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 !rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="p-2">
              {moreMenuItems.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/' && location.pathname.startsWith(item.href));
                
                return (
                  <button
                    key={item.name}
                    onClick={() => handleMenuItemClick(item.href)}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium !rounded-xl transition-colors ${
                      isActive
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                    <span>{item.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientBottomNav;
